import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import CachedIcon from "@mui/icons-material/Cached";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import {
	Button,
	ButtonGroup,
	Card,
	CardContent,
	CardHeader,
	List,
	ListItem,
	ListItemText,
	Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { useTime } from "@/components/Application/TimeContext";
import { Translate } from "@/components/Application/Translate";
import DocumentSearchModal from "@/components/Procedures/EditNode/DocumentSearchModal";

import { DocumentUpdateRequest } from "@/model/Procedure/DocumentUpdateRequest";
import { NodeEditSection } from "@/model/Procedure/EditNode";
import { ProcedureDetailNode } from "@/model/Procedure/ProcedureDetailNode";
import {
	DocumentFromProcedure,
	DocumentSourceTypeAsNumber,
} from "@/model/Procedure/ProcedureUseDocument";

import { ReimportDocument } from "@/service/ProcedureMutations";
import { useFetchDocumentForCurrentNode } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

dayjs.extend(utc);

interface NodeAttachedDocumentInformationProps {
	procedureId: number;
	node: ProcedureDetailNode | undefined;
	editSection: NodeEditSection;
}

export const NodeAttachedDocumentInformation: FC<
	NodeAttachedDocumentInformationProps
> = (props) => {
	const { id } = useParams<RouteParams>();

	const procedureId = parseInt(id);
	const [document, setDocument] = useState<
		DocumentFromProcedure | null | undefined
	>(undefined);
	const [docSearchModalOpen, toggleDocSearchModal] = useState<boolean>(false);
	const [onNodeIdChange, setNodeIdChange] = useState(true);
	const [updateLoading, setDocLoading] = useState<boolean>(false);
	const [documentUpdated, setDocUpdated] = useState<boolean>(false);

	const { isLoading, data, refetch } = useFetchDocumentForCurrentNode({
		ProcedureId: procedureId,
		SchemeId: props.node?.SchemeId,
	});

	const reimportDocument = ReimportDocument();

	const handleUpdateDocument = async () => {
		setDocLoading(true);
		try {
			await new Promise((resolve) => {
				setTimeout(resolve, 2000); // 2000 milliseconds (2 seconds)
			});
			const documentUpdateRequest: DocumentUpdateRequest = {
				FileId: data?.Document?.FileId!,
				ExternalUniqueId: props.node?.ExternalUniqueId ?? "",
				IsReload: true,
				ProcedureId: props.procedureId,
			};
			await reimportDocument(documentUpdateRequest);
			setDocUpdated(true);
		} catch (error) {
			console.error(error);
		} finally {
			setDocLoading(false);
			await new Promise((resolve) => {
				setTimeout(resolve, 2000); // 2000 milliseconds (2 seconds)
			});
			setDocUpdated(false);
		}
	};

	const closeDocSearch = () => toggleDocSearchModal(false);

	useEffect(() => {
		if (!data) {
			refetch().then((res) => setDocument(res.data?.Document));
		}
	}, [isLoading, data, props.node?.FileName]);

	useEffect(() => {
		refetch().then((res) => setDocument(res.data?.Document));
	}, [props.node]);

	useEffect(() => {
		if (data && data.Document && props.node && onNodeIdChange) {
			const inDocument: DocumentFromProcedure = {
				FileId: props.node.FileId,
				FileName: props.node.FileName,
				Folder: props.node.FilePath,
				FileLinkId: 0,
				DuplicatedDocument: false,
				FileDate: props.node.FileUpdated,
				ExternalUniqueId: props.node.ExternalUniqueId,
				ProcedureList: data.Document.ProcedureList,
				DocumentLocationType: data.Document.DocumentLocationType,
				UrlSource: data.Document.UrlSource,
				Source: data.Document.Source,
			};
			setDocument(inDocument);
			setNodeIdChange(false);
		}
	}, [data, props.node, onNodeIdChange]);

	const docNameEndIndex: number | undefined =
		document?.FileName?.lastIndexOf("/") + 1;

	const prettyDocName = document?.FileName.substring(docNameEndIndex);
	const { timezone } = useTime();

	return (
		<Card style={{ boxShadow: "none" }} className="card card--default">
			<CardHeader
				title={
					<Translate String="Procedure.Node.Edit.Document">
						Attached Document
					</Translate>
				}
				titleTypographyProps={{
					fontWeight: "bold",
					marginTop: "none",
				}}
				subheader={
					document && (
						<>
							<Translate String="Procedure.Node.Document.Updated">
								Updated
							</Translate>{" "}
							:{" "}
							{dayjs
								.utc(document?.FileDate)
								.tz(timezone)
								.format("DD/MM/YY HH:mm")}
						</>
					)
				}
			/>

			<CardContent>
				<>
					<ButtonGroup className="card--default--actions">
						<Button
							variant="outlined"
							color="info"
							onClick={() => {
								toggleDocSearchModal(true);
							}}
						>
							<EditIcon />
						</Button>
					</ButtonGroup>
					{document && (
						<>
							<Translate
								String="Procedures.UsedDocuments.FileDetails"
								HTMLTag="h5"
							>
								File Details
							</Translate>
							<List>
								<ListItem>
									<ListItemText>
										<Translate String="Procedures.UsedDocuments.Name">
											Name
										</Translate>
										: {prettyDocName}
									</ListItemText>
									{document.Source !== DocumentSourceTypeAsNumber.Local ? (
										<Tooltip
											title={
												<Translate String="Procedures.UsedDocuments.Update.Document">
													Update document
												</Translate>
											}
										>
											<Button
												variant="text"
												color={documentUpdated ? "success" : "info"}
												onClick={handleUpdateDocument}
											>
												{documentUpdated ? (
													<CheckIcon />
												) : (
													<CachedIcon
														sx={{
															animation: updateLoading
																? "spin 2s linear infinite"
																: "",
															"@keyframes spin": {
																"0%": {
																	transform: "rotate(360deg)",
																},
																"100%": {
																	transform: "rotate(0deg)",
																},
															},
														}}
													/>
												)}
											</Button>
										</Tooltip>
									) : (
										<></>
									)}
								</ListItem>
								<ListItem>
									{document.UrlSource ? (
										<a
											href={`/api/Documents/${document.FileId}/html`}
											className="link"
											target="_blank"
											rel="noopener noreferrer"
										>
											<ListItemText className="link">
												<Translate String="Procedures.UsedDocuments.Link">
													Link
												</Translate>
												: {prettyDocName}
											</ListItemText>
										</a>
									) : (
										<></>
									)}
								</ListItem>
							</List>
						</>
					)}
				</>
			</CardContent>
			<DocumentSearchModal
				open={docSearchModalOpen}
				onClose={closeDocSearch}
				nodeId={props.node?.SchemeId ?? 0}
			/>
		</Card>
	);
};
